import { useState, useEffect, useRef, createRef, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Combobox from '@Components/MuiWrappers/Combobox';
import PhoneField from '@Components/MuiWrappers/PhoneField';
import PasswordField from '@Components/MuiWrappers/PasswordField';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import WidgetDisplay from '@Components/Shared/Widgets/WidgetDisplay';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { gtmPushEvent, sandhillsRequest, setLoanApprovalEstimateNewRegistrant, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType, userCreationType, magazineDistributionPublications, mfaDisplayModes, mfaMethodSource } from '@Components/Utilities/SandhillsConstants';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import AddressValidationModal from '@Components/Shared/Modals/AddressValidationModal';
import GoogleAutoComplete from '@Components/Shared/Controls/GoogleAutoComplete'
import SandhillsReCAPTCHA from '@Components/Shared/SandhillsReCAPTCHA';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { styled } from '@mui/material/styles';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import MultiFactorModal from '@Components/Shared/Modals/MultiFactorModal';
import TextSignUpModal from '@Components/Shared/Modals/TextSignUpModal';

const RegistrationPaper = styled(Paper)({
    marginBottom: '15px'
});

const TypographyTitle = styled(Typography)({
    margin: '15px 0px'
});

const TypographyText = styled(Typography)({
    fontSize: '14px'
});

const TypographyError = styled(TypographyText)(({ theme }) => ({
    color: theme.palette.error.main
}));

const CheckboxGrid = styled(Grid)({
    display: 'flex',
    alignItems: 'center'
});

const CheckboxField = styled(Checkbox)({
    alignSelf: 'flex-start',
    padding: '0px'        
});

const SubmitButton = styled(Button)({
    width: '100%'    
});

const SpanError = styled('span')(({ theme }) => ({
    color: theme.palette.error.main
}));

const RegistrationForm = (props) => {

    const recaptchaRef = createRef();
    const [captchaOpen, setCaptchaOpen] = useState(false);
    const intlTelInput = useRef(null);
    const dealerIntlTelInput = useRef(null);
    const inputReferences = useRef({});
    const countries = props.GetCountryDropDown || [];
    const countriesThatRequireState = props.GetCountriesThatRequireState || [];
    const countriesThatRequirePostalCode = props.GetCountriesThatRequirePostalCode || [];
    const [saveRegistration, setSaveRegistration] = useState(false);
    const [updateSuccessful, setUpdateSuccessful] = useState(false);
    const [emailValid, setEmailValid] = useState(true);
    const [emailSuggestion, setEmailSuggestion] = useState('');
    const [showLoginSuggestion, setShowLoginSuggestion] = useState(false);
    const [showInvalidEmail, setShowInvalidEmail] = useState(false);
    const [showEmailIsTooLong, setShowEmailIsTooLong] = useState(false);
    const [validAddress, setValidAddress] = useState(false);
    const [addressValidated, setAddressValidated] = useState(false);
    const [showAddressValidationModal, setShowAddressValidationModal] = useState(false);
    const [showTextSignUp, setShowTextSignUp] = useState(props.WeeklyTextSubVisible && !props.SubscribeToWeeklyText);
    const [showMFAModal, setShowMFAModal] = useState(false);
    const [provideAdditionalInfo, setProvideAdditionalInfo] = useState(props.AdditionalInfoRequired || (props.UserCreationTypeID == userCreationType.notAddedByExternalAuth) || (props.UserCreationTypeID == userCreationType.externalAuthAddedWithCleanData));
    const [validationErrors, setValidationErrors] = useState({});
    const [subscribeToWeeklyText, setSubscribeToWeeklyText] = useState(props.SubscribeToWeeklyText);
    const [requestedPhoneVerification, setRequestedPhoneVerification] = useState(false);
    const [mfaNotFinished, setMfaNotFinished] = useState(false);
    const [mfaCanSendTexts, setMfaCanSendTexts] = useState(true);
    const [mfaPhoneValidationFinished, setMfaPhoneValidationFinished] = useState(false);
    const [showTsuModal, setShowTsuModal] = useState(false);
    const [redirectURL, setRedirectURL] = useState('');
    const [centralUserAuthid, setCentralUserAuthid] = useState(props.CentralUserAuthid);
    const [pendingSubscriptionToken, setPendingSubscriptionToken] = useState('');
    const [formData, setFormData] = useState({
        Email: props.Email || '',
        Firstname: props.Firstname || '',
        Lastname: props.Lastname || '',
        Password: props.Password || '',
        CompanyName: props.CompanyName || '',
        Address1: props.Address1 || '',
        Address2: props.Address2 || '',
        Address3: props.Address3 || '',
        City: props.City || '',
        State: props.State || '',
        StateTyped: props.StateTyped || '',
        States: [],
        StateRequired: false,
        PostalCode: props.PostalCode || '',
        PostalCodeRequired: false,
        Country: props.Country || '',
        Phone: props.Phone || '',
        EmailSubAndAcknowledgement: props.EmailSubAndAcknowledgement || {},
        UserCreationTypeID: props.UserCreationTypeID,
        FreeMagazine: false,
        // Dealer Reference
        DealerReferenceContactName: props.DealerReferenceContactName || '',
        DealerReferenceDealerName: props.DealerReferenceDealerName || '',
        DealerReferenceAddress1: props.DealerReferenceAddress1 || '',
        DealerReferenceAddress2: props.DealerReferenceAddress2 || '',
        DealerReferenceCity: props.DealerReferenceCity || '',
        DealerReferenceState: props.DealerReferenceState || '',
        DealerReferencePostalCode: props.DealerReferencePostalCode || '',
        DealerPostalCodeRequired: false,
        DealerReferenceCountry: props.DealerReferenceCountry || '',
        DealerReferencePhone: props.DealerReferencePhone || '',
        DealerReferenceStateTyped: props.DealerReferenceStateTyped || '',
        DealerReferenceStates: [],
        DealerStateRequired: false,
        BannedCountryError: props.BannedCountryError || ''
    });

    const [recommendedAddress, setRecommendedAddress] = useState({
        AddressError: '',
        Address1: '',
        Address2: '',
        Address3: '',
        City: '',
        State: '',
        PostalCode: '',
        Country: ''
    });
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);

    useEffect(() => {
        inputReferences.current?.['Email']?.focus?.();
        const stateRequired = formData.Country ? countriesThatRequireState.some(c => c.toUpperCase() == formData.Country.toUpperCase()) : false;
        if (stateRequired) {
            getStatesByCountry(formData.Country);
        }
        
        setFormData(previousState => {
            return {
                ...previousState,
                StateRequired: stateRequired,
                PostalCodeRequired: formData.Country ? countriesThatRequirePostalCode.some(c => c.toUpperCase() == formData.Country.toUpperCase()) : false,
                EmailSubAndAcknowledgement: {
                    ...previousState.EmailSubAndAcknowledgement,
                    SubscribeToWeeklyUpdate: props.NewRegistration ? (props.DefaultCountry == 'USA') : previousState.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate
                }
            };
        });
    }, []);

    useEffect(function executeReCaptcha() {
        const recaptchaReady = props.Captcha && recaptchaRef.current;
        if (saveRegistration) {
            if (props.NewRegistration && recaptchaReady) {
                setCaptchaOpen(true);
                recaptchaRef.current.execute();
            } else {
                onSubmit();
            }
        } else if (props.NewRegistration && recaptchaReady) {
            setCaptchaOpen(false);
            recaptchaRef.current.reset();
        }
    }, [saveRegistration]);

    useEffect(function toggleFields() {
        if (provideAdditionalInfo) {
            if ((props.UserCreationTypeID == userCreationType.minimalAuthAddedWithPlaceholderData) || (props.UserCreationTypeID == userCreationType.externalAuthAddedWithPlaceholderData)) {
                setValidAddress(false);
                setFormData(previousState => {
                    return {
                        ...previousState,
                        CompanyName: '',
                        Address1: '',
                        Address2: '',
                        Address3: '',
                        City: '',
                        State: '',
                        StateTyped: '',
                        PostalCode: '',
                        Phone: props.Phone && /\d/.test(props.Phone) ? props.Phone : '',
                        States: [],
                        Country: props.DefaultCountry,
                        UserCreationTypeID: (props.UserCreationTypeID == userCreationType.externalAuthAddedWithPlaceholderData) ? userCreationType.externalAuthAddedWithCleanData : userCreationType.notAddedByExternalAuth,
                        FreeMagazine: props.NewRegistration,
                        StateRequired: props.DefaultCountry ? countriesThatRequireState.some(c => c.toUpperCase() == props.DefaultCountry.toUpperCase()) : false,
                        PostalCodeRequired: props.DefaultCountry ? countriesThatRequirePostalCode.some(c => c.toUpperCase() == props.DefaultCountry.toUpperCase()) : false,
                        EmailSubAndAcknowledgement: {
                            ...previousState.EmailSubAndAcknowledgement,
                            SubscribeToWeeklyUpdate: props.NewRegistration ? (props.DefaultCountry == 'USA') : previousState.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate
                        }
                    };
                });
            }
        } else {
            setFormData(previousState => {
                return {
                    ...previousState,
                    CompanyName: '',
                    Address1: '',
                    Address2: '',
                    Address3: '',
                    City: '',
                    State: '',
                    StateTyped: '',
                    PostalCode: '',
                    Country: '',
                    Phone: '',
                    States: [],
                    StateRequired: false,
                    PostalCodeRequired: false,
                    UserCreationTypeID: props.UserCreationTypeID,
                    FreeMagazine: false
                };
            });
        }
    }, [provideAdditionalInfo]);

    const submitForm = (e) => {
        e?.preventDefault?.();
        setLoanApprovalEstimateNewRegistrant();
        
        let rules = [
            ruleRunner('Email', translator.translate('sandhills.email'), ValidationRules.required),
            ruleRunner('Email', translator.translate('sandhills.email'), ValidationRules.validEmail),
            ruleRunner('Firstname', translator.translate('sandhills.tradepubwebsite.mvc.firstname'), ValidationRules.required),
            ruleRunner('Lastname', translator.translate('sandhills.tradepubwebsite.mvc.lastname'), ValidationRules.required)
        ];

        if (props.NewRegistration) {
            rules.push(ruleRunner('Password', translator.translate('sandhills.password'), ValidationRules.required));

            if ((currentSite?.Site.IsBidcaller || currentSite?.Site.IsAuctionTime ) && formData.Country.toUpperCase() == 'MEXICO') {

                rules.push(ruleRunner('DealerReferenceDealerName', translator.translate('sandhills.dealer'), ValidationRules.required));
                rules.push(ruleRunner('DealerReferenceContactName', translator.translate('sandhills.contact'), ValidationRules.required));
                rules.push(ruleRunner('DealerReferenceCity', translator.translate('sandhills.city'), ValidationRules.required));
                rules.push(ruleRunner('DealerReferenceCountry', translator.translate('sandhills.country'), ValidationRules.required));
                rules.push(ruleRunner('DealerReferenceAddress1', translator.translate('sandhills.tradepubwebsite.mvc.address'), ValidationRules.required));
                rules.push(ruleRunner('DealerReferencePhone', translator.translate('sandhills.phonenumber'), ValidationRules.required));

                if(formData.DealerStateRequired) {
                    rules.push(ruleRunner('DealerReferenceState', translator.translate('sandhills.stateprovince'), ValidationRules.required));
                }
                if (formData.DealerPostalCodeRequired) {
                    rules.push(ruleRunner('DealerReferencePostalCode', translator.translate('sandhills.tradepubwebsite.mvc.postalcode'), ValidationRules.required));
                }
            }
        }

        if (provideAdditionalInfo) {
            rules.push(ruleRunner('Country', translator.translate('sandhills.country'), ValidationRules.required));
            rules.push(ruleRunner('Address1', translator.translate('sandhills.tradepubwebsite.mvc.address'), ValidationRules.required));
            rules.push(ruleRunner('City', translator.translate('sandhills.city'), ValidationRules.required));

            if (formData.StateRequired) {
                rules.push(ruleRunner('State', translator.translate('sandhills.stateprovince'), ValidationRules.required));
            }

            if (formData.PostalCodeRequired) {
                rules.push(ruleRunner('PostalCode', translator.translate('sandhills.tradepubwebsite.mvc.postalcode'), ValidationRules.required));
            }

            rules.push(ruleRunner('Phone', translator.translate('sandhills.phonenumber'), ValidationRules.required));
        }

        const errors = run(formData, rules);

        if (!formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted && formData.EmailSubAndAcknowledgement.PrivacyPolicyStatementVisible) {
            errors.PrivacyPolicyAccepted = translator.translate('sandhills.required').toString();
        }

        if (!emailValid) {
            errors.Email = translator.translate('sandhills.registration.invalidemail').toString();
        }

        if (provideAdditionalInfo && !errors?.Phone && intlTelInput.current?.isValidNumber() == false) {
            errors.Phone = translator.translate('sandhills.invalidphone').toString();
        }

        if ((currentSite?.Site.IsBidcaller || currentSite?.Site.IsAuctionTime) && formData.Country.toUpperCase() == 'MEXICO' && !errors?.DealerReferencePhone && dealerIntlTelInput.current?.isValidNumber() == false) {
            errors.DealerReferencePhone = translator.translate('sandhills.invalidphone').toString();
        }

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);

        if (Object.keys(errors).length <= 0) {
            if (provideAdditionalInfo && !showLoginSuggestion && !validAddress) {
                validateAddress();
            } else if (mfaNotFinished) {
                mfaCheckReVerificationNeeded();
            } else {
                setUpdateSuccessful(false);
                setSaveRegistration(true);
            }
        }
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.Registration,
            method: 'post',
            data: {
                Email: formData.Email,
                Firstname: formData.Firstname,
                Lastname: formData.Lastname,
                Password: formData.Password,
                Address1: formData.Address1,
                Address2: formData.Address2,
                Address3: formData.Address3,
                City: formData.City,
                State: formData.State,
                StateTyped: formData.StateTyped,
                PostalCode: formData.PostalCode,
                Country: formData.Country,
                Phone: intlTelInput.current?.getNumber() ?? formData.Phone,
                AlternatePhone: props.AlternatePhone,
                CompanyName: formData.CompanyName,
                UserCreationTypeID: formData.UserCreationTypeID,
                FreeMagazine: formData.FreeMagazine,
                SiteID: props.SiteID,
                timeZone: props.timeZone,
                PreferredLanguageID: props.PreferredLanguageID || 2,
                NewRegistration: props.NewRegistration,
                to: props.ToURL,
                mob: props.MobURL,
                AddressValidated: addressValidated ? '1' : '0',
                Captcha: {
                    Response: token
                },
                SubscribeToWeeklyText: subscribeToWeeklyText,
                WeeklyTextSubVisible: props.WeeklyTextSubVisible,
                EmailSubAndAcknowledgement: {
                    WeeklyUpdateVisible: formData.EmailSubAndAcknowledgement.WeeklyUpdateVisible,
                    PrivacyPolicyStatementVisible: formData.EmailSubAndAcknowledgement.PrivacyPolicyStatementVisible,
                    SubscribeToWeeklyUpdate: formData.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate,
                    PrivacyPolicyAccepted: formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted,
                    WebAgreementTypeIDs: formData.EmailSubAndAcknowledgement.WebAgreementTypeIDs,
                    PixelsPolicyStatementVisible: formData.EmailSubAndAcknowledgement.PixelsPolicyStatementVisible,
                    PixelsPolicyAccepted: formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted
                },
                RequestedPhoneVerification: requestedPhoneVerification,
                DealerReferenceContactName: formData.DealerReferenceContactName,
                DealerReferenceDealerName: formData.DealerReferenceDealerName,
                DealerReferenceAddress1: formData.DealerReferenceAddress1,
                DealerReferenceAddress2: formData.DealerReferenceAddress2,
                DealerReferenceCity: formData.DealerReferenceCity,
                DealerReferenceState: formData.DealerReferenceState,
                DealerReferencePostalCode: formData.DealerReferencePostalCode,
                DealerReferenceCountry: formData.DealerReferenceCountry,
                DealerReferencePhone: dealerIntlTelInput.current?.getNumber() ?? formData.DealerReferencePhone,
                DealerReferenceStateTyped: formData.DealerReferenceStateTyped,
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            if (data) {                
                const errors = data.ValidationErrors || {};
                setSaveRegistration(false);
                if (data.RedirectURL) {
                    gtmPushEvent(gtmEventType.newRegistration);
                    if (showTextSignUp && Object.keys(errors).length === 0) {
                        setShowTsuModal(true);
                        setRedirectURL(data.RedirectURL);
                        setCentralUserAuthid(data.CentralUserAuthid);
                        setPendingSubscriptionToken(data.PendingSubscriptionToken || '');
                    }
                    else {
                        window.location.href = data.RedirectURL;
                    }
                } else if (data.BannedCountryError) {
                    setFormData(previousState => {
                        return {
                            ...previousState,
                            BannedCountryError: data.BannedCountryError || ''
                        }
                    }); 
                } else {
                    setFormData(previousState => {
                        return {
                            ...previousState,
                            CompanyName: data.CompanyName || '',
                            Firstname: data.Firstname || '',
                            Lastname: data.Lastname || '',
                            Address1: data.Address1 || '',
                            Address2: data.Address2 || '',
                            Address3: data.Address3 || '',
                            City: data.City || '',
                            State: data.State || '',
                            StateTyped: data.StateTyped || '',
                            PostalCode: data.PostalCode || '',
                            Country: data.Country || '',
                            Phone: data.Phone || '',
                            Email: data.Email || '',
                            Password: data.Password || '',
                            EmailSubAndAcknowledgement: {
                                ...previousState.EmailSubAndAcknowledgement,
                                SubscribeToWeeklyUpdate: data.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate,
                                PrivacyPolicyAccepted: data.EmailSubAndAcknowledgement.PrivacyPolicyAccepted,
                                PixelsPolicyAccepted: data.EmailSubAndAcknowledgement.PixelsPolicyAccepted
                            }
                        };
                    });
                    setUpdateSuccessful(!props.NewRegistration && data.Success);
                    setShowInvalidEmail(false);
                    setEmailSuggestion('');
                    setShowLoginSuggestion(false);
                    setValidationErrors(errors);
                    focusErroredField(errors, inputReferences);
                    if (showTextSignUp && Object.keys(errors).length === 0) {
                        setShowTsuModal(true);
                        setRedirectURL(data.RedirectURL);
                        setCentralUserAuthid(data.CentralUserAuthid);
                        setPendingSubscriptionToken(data.PendingSubscriptionToken || '');
                    }
                }
            }
        });
    };

    const closeModalAndSubmit = (data, validated) => {
        setFormData(previousState => {
            return {
                ...previousState,
                Address1: data.Address1,
                Address2: data.Address2 ? data.Address2 : '',
                Address3: data.Address3 ? data.Address3 : '',
                City: data.City,
                State: data.State ? data.State : data.StateOrProvince,
                PostalCode: data.PostalCode,
                Country: data.Country
            };
        });
        setAddressValidated(validated);
        setShowAddressValidationModal(false);
        setValidAddress(true);
        mfaCheckReVerificationNeeded();
    };

    const validateAddress = () => {
        sandhillsRequest({
            url: routes.ValidateAddress,
            method: 'post',
            data: {
                address1: formData.Address1,
                address2: formData.Address2,
                address3: formData.Address3,
                city: formData.City,
                stateOrProvince: formData.State,
                postalCode: formData.PostalCode,
                country: formData.Country
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                if (response.SandhillsAction) {
                    if (response.SandhillsAction == 'ValidNoActionNeeded') {
                        setAddressValidated(true);
                        setValidAddress(true);
                        mfaCheckReVerificationNeeded();
                    } else if (response.SandhillsAction == 'SilentCorrection') {
                        setFormData(previousState => {
                            return {
                                ...previousState,
                                Address1: response.Address1,
                                Address2: response.Address2 ? response.Address2 : '',
                                Address3: response.Address3 ? response.Address3 : '',
                                City: response.City,
                                State: response.StateOrProvince,
                                PostalCode: response.PostalCode,
                                Country: response.Country
                            }
                        });
                        setAddressValidated(true);
                        setValidAddress(true);
                        mfaCheckReVerificationNeeded();
                    } else if (response.SandhillsAction == 'PromptUser') {
                        setRecommendedAddress({
                            AddressError: response.AddressError,
                            Address1: response.Address1,
                            Address2: response.Address2 ? response.Address2 : '',
                            Address3: response.Address3 ? response.Address3 : '',
                            City: response.City,
                            State: response.StateOrProvince,
                            PostalCode: response.PostalCode,
                            Country: response.Country
                        });
                        setShowAddressValidationModal(true);
                    }
                }
            }
        });
    };

    const checkEmail = (e) => {
        const email = e.target ? e.target.value : e;
        setFormData(previousState => {
            return { ...previousState, Email: email };
        });

        setEmailSuggestion('');
        setShowLoginSuggestion(false);
        setShowInvalidEmail(false);
        setShowEmailIsTooLong(false);
        setValidationErrors(previousState => {
            return { ...previousState, Email: '' };
        });

        if (email) {
            const regex = new RegExp(props.EmailRegex, 'i');
            if (regex.test(email)) {
                sandhillsRequest({
                    url: routes.CheckDuplicateCredentials,
                    method: 'post',
                    data: {
                        'sEmail': email
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((response) => {
                    if (response) {
                        setShowLoginSuggestion(true);
                    } else {
                        sandhillsRequest({
                            url: routes.ValidateEmail,
                            method: 'post',
                            data: {
                                'sEmail': email
                            },
                            errorHandling: {
                                useConsole: false,
                                useAlert: false
                            }
                        }).then((response) => {
                            if (response.EmailIsTooLong) {
                                setShowEmailIsTooLong(true);
                                setShowInvalidEmail(false);
                                setEmailValid(false);
                            } else {
                                setShowEmailIsTooLong(false);
                                if (response.IsValidEmail) {
                                    setShowInvalidEmail(false);
                                    setEmailValid(true);
                                } else {
                                    setShowInvalidEmail(true);
                                    setEmailValid(false);
                                }
                            }

                            if (response.DidYouMean) {
                                setEmailSuggestion(response.DidYouMean);
                            }
                        }).catch(() => {
                            setShowInvalidEmail(false);
                            setEmailValid(true);
                        });
                    }
                });
            } else {
                setShowInvalidEmail(true);
            }
        }
    };

    const warnBeforeUnload = useCallback((e) => { e.preventDefault() }, []);

    const toggleMultiFactorModal = (authorized) => {
        !showMFAModal ? window.addEventListener("beforeunload", warnBeforeUnload) : window.removeEventListener("beforeunload", warnBeforeUnload);
        setMfaNotFinished(showMFAModal && !authorized);
        setShowMFAModal(!showMFAModal);
    }

    const mfaCheckReVerificationNeeded = () => {
        if (props.NewRegistration) {
            setSaveRegistration(true);
        } else {
            sandhillsRequest({
                url: routes.MFACheckReVerificationNeeded,
                method: 'post',
                data: {
                    phone: formData.Phone
                },
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((response) => {
                if (response) {
                    checkPhoneNumberIsMobile();
                } else {
                    setSaveRegistration(true);
                }
            });
        }
    }

    const onMultiFactorAuthorized = (multifactorAuthID) => {
        toggleMultiFactorModal(true);
        setRequestedPhoneVerification(multifactorAuthID === 0);
        setSaveRegistration(true);
    }

    const checkPhoneNumberIsMobile = () => {
        setMfaPhoneValidationFinished(false);
        setMfaCanSendTexts(true);
        sandhillsRequest({
            url: routes.CheckPhoneNumberIsMobile,
            method: 'post',
            data: {
                phoneNumber: formData.Phone
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response) {
                setMfaCanSendTexts(response.CanSendTexts);
            } else {
                //If the API call errors, allow user to attempt to send texts
                setMfaCanSendTexts(true);
            }
        }).catch((error) => {
            console.log(error);
            //If the API call errors, allow user to attempt to send texts
            setMfaCanSendTexts(true);
        }).finally(() => {
            setMfaPhoneValidationFinished(true);
        });
        toggleMultiFactorModal();
    }

    const onChange = (e, option) => {
        const name = e.target.name;
        const value = option?.value ?? e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
        setValidationErrors(previousState => {
            return { ...previousState, [name]: '' };
        });
    };

    const onPlaceChange = (e) => {
        getStatesByCountry(e.Country);
        setFormData(previousState => {
            return {
                ...previousState,
                Address1: e.Address1,
                City: e.City,
                State: e.State,
                StateTyped: e.StateTyped,
                PostalCode: e.PostalCode,
                Country: e.Country,
                StateRequired: e.Country ? countriesThatRequireState.some(c => c.toUpperCase() == e.Country.toUpperCase()) : false,
                PostalCodeRequired: e.Country ? countriesThatRequirePostalCode.some(c => c.toUpperCase() == e.Country.toUpperCase()) : false,
                EmailSubAndAcknowledgement: {
                    ...previousState.EmailSubAndAcknowledgement,
                    SubscribeToWeeklyUpdate: props.NewRegistration ? (e.Country == 'USA') : previousState.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate
                }
            };
        });

        setValidationErrors(previousState => {
            return {
                ...previousState,
                Address1: '',
                City: '',
                State: '',
                PostalCode: '',
                Country: ''
            };
        });
    };

    const onCountryChange = (e, option) => {
        const value = option?.value ?? '';
        setValidAddress(false);
        setFormData(previousState => {
            return {
                ...previousState,
                Country: value,
                State: '',
                StateTyped: '',
                States: [],
                Address3: value ? ((value.toUpperCase() == 'USA' || value.toUpperCase() == 'CANADA') ? '' : previousState.Address3) : '',
                StateRequired: value ? countriesThatRequireState.some(c => c.toUpperCase() == value.toUpperCase()) : false,
                PostalCodeRequired: value ? countriesThatRequirePostalCode.some(c => c.toUpperCase() == value.toUpperCase()) : false,
                EmailSubAndAcknowledgement: {
                    ...previousState.EmailSubAndAcknowledgement,
                    SubscribeToWeeklyUpdate: props.NewRegistration ? (value == 'USA') : previousState.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate
                }
            };
        });

        setValidationErrors(previousState => {
            return {
                ...previousState,
                Country: '',
                State: ''
            };
        });
    };

    const onStateChange = (e, option) => {
        const value = option?.value ?? '';
        setValidAddress(false);
        setFormData(previousState => {
            return {
                ...previousState,
                State: value,
                StateTyped: ''
            };
        });

        setValidationErrors(previousState => {
            return {
                ...previousState,
                State: ''
            };
        });
    };

    const onDealerCountryChange = (e, option) => {
        const value = option?.value ?? '';
        setValidAddress(false);
        setFormData(previousState => {
            return {
                ...previousState,
                DealerReferenceCountry: value,
                DealerReferenceState: '',
                DealerReferenceStateTyped: '',
                DealerReferenceStates: [],
                DealerStateRequired: value ? countriesThatRequireState.some(c => c.toUpperCase() == value.toUpperCase()) : false,
                DealerPostalCodeRequired: formData.DealerReferenceCountry ? countriesThatRequirePostalCode.some(c => c.toUpperCase() == formData.DealerReferenceCountry.toUpperCase()) : false,
            };
        });

        setValidationErrors(previousState => {
            return {
                ...previousState,
                DealerReferenceCountry: '',
                DealerReferenceState: ''
            };
        });
    };

    const onDealerStateChange = (e, option) => {
        const value = option?.value ?? '';
        setValidAddress(false);
        setFormData(previousState => {
            return {
                ...previousState,
                DealerReferenceState: value,
                DealerReferenceStateTyped: ''
            };
        });

        setValidationErrors(previousState => {
            return {
                ...previousState,
                DealerReferenceState: ''
            };
        });
    };

    const onCheckboxChange = (e) => {
        const checked = e.target.checked;
        const stateName = e.target.name || e.target.id;
        setFormData(previousState => {
            return {
                ...previousState,
                EmailSubAndAcknowledgement: {
                    ...previousState.EmailSubAndAcknowledgement,
                    [stateName]: checked
                }
            };
        });

        setValidationErrors(previousState => {
            return { ...previousState, [stateName]: '' };
        });
    };

    const onAddressChange = (e) => {
        onChange(e);
        setValidAddress(false);
    };

    const onAddressBlur = () => {
        setValidAddress(false);
    };

    const getStatesByCountry = (country) => {
        return sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': country,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince')
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then(response => {
            setFormData(previousState => {
                return { ...previousState, States: response || [] };
            });
        });
    };

    const getDealerStatesByCountry = (country) => {
        return sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': country,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince')
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then(response => {
            setFormData(previousState => {
                return { ...previousState, DealerReferenceStates: response || [] };
            });
        });
    };

    const resetSubmit = () => {
        setSaveRegistration(false);
        setShowInvalidEmail(false);
        setShowLoginSuggestion(false);
    };

    const displayAdditionalInfoOption = () => {
        return (!props.AdditionalInfoRequired &&
            (!props.NewRegistration ||
                (currentSite?.TopLevelDomain == '.com' &&
                    magazineDistributionPublications.some(publication => publication.toLowerCase() == currentSite?.CurrentSite?.toLowerCase?.()))) &&
            ((props.UserCreationTypeID == userCreationType.minimalAuthAddedWithPlaceholderData) ||
                (props.UserCreationTypeID == userCreationType.externalAuthAddedWithPlaceholderData))
        );
    };

    const getEmailHelperText = () => {
        if (validationErrors.Email) {
            return validationErrors.Email;
        } else if (emailSuggestion) {
            return <>{translator.translate('sandhills.registration.invalidemail') + " " + translator.translate('sandhills.registration.emailsuggesttext').replace('{0}?', ' ')}<Link onClick={() => checkEmail(emailSuggestion)}>{emailSuggestion}</Link>?</>;
        } else if (showEmailIsTooLong) {
            return translator.translate('sandhills.emailtoolong');
        } else if (showInvalidEmail) {
            return translator.translate('sandhills.registration.invalidemail');
        } else if (showLoginSuggestion) {
            return <>{translator.translate('sandhills.registration.alreadyregistered')} <Link color='error' href={props.ForgotPasswordURL}>{translator.translate('sandhills.passwordchange.forgotpassword')}</Link></>;
        }
        return undefined;
    };

    const getOptionLabel = (option) => {
        return option.text || option;
    };

    const isOptionEqualToValue = (option, value) => {
        return option?.value == value;
    };

    const getOptions = (options) => {
        return options.map((item, index) => {
            return {
                key: `${item.Text}-${item.Value || 'Empty'}-${index}`,
                text: item.Text,
                value: item.Value
            };
        });
    };

    const requiredLabel = (label) => {
        return (<><SpanError>* </SpanError>{label}</>);
    };

    const SubscriptionAndAcknowledgement = () => {
        return (
            <>
                {formData.EmailSubAndAcknowledgement.WeeklyUpdateVisible &&
                    <CheckboxGrid item xs={12}>
                        <CheckboxField
                            size='small'
                            id='SubscribeToWeeklyUpdate'
                            className='registration-checkbox'
                            checked={formData.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate}
                            onChange={onCheckboxChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    onCheckboxChange({
                                        target: {
                                            id: 'SubscribeToWeeklyUpdate',
                                            checked: !formData.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate
                                        }
                                    });
                                }
                            }}
                            inputProps={{
                                'aria-labelledby': 'sub-weekly-update',
                            }}
                        />
                        <TypographyText id='sub-weekly-update' dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.EmailSubText }} sx={{ paddingLeft: '5px' }} />
                    </CheckboxGrid>}
                {formData.EmailSubAndAcknowledgement.PixelsPolicyStatementVisible &&
                    <CheckboxGrid item xs={12}>
                        <CheckboxField
                            size='small'
                            id='PixelsPolicyAccepted'
                            name='PixelsPolicyAccepted'
                            className='registration-checkbox'
                            checked={formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted}
                            onChange={onCheckboxChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    onCheckboxChange({
                                        target: {
                                            id: 'PixelsPolicyAccepted',
                                            checked: !formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted
                                        }
                                    });
                                }
                            }}
                            inputProps={{
                                'aria-labelledby': 'pixels-policy-consent',
                            }}
                        />
                        <TypographyText id='pixels-policy-consent' dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PixelsPolicyText }} sx={{ paddingLeft: '5px' }} />
                    </CheckboxGrid>}
                {formData.EmailSubAndAcknowledgement.PrivacyPolicyStatementVisible &&
                    <CheckboxGrid item xs={12}>
                        {formData.EmailSubAndAcknowledgement.PrivacyPolicyIsCheckbox ?
                            <>
                                <CheckboxField
                                    size='small'
                                    id='PrivacyPolicyAccepted'
                                    inputRef={element => inputReferences.current['PrivacyPolicyAccepted'] = element}
                                    checked={formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted}
                                    onChange={onCheckboxChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            onCheckboxChange({
                                                target: {
                                                    id: 'PrivacyPolicyAccepted',
                                                    checked: !formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted
                                                }
                                            });
                                        }
                                    }}
                                    inputProps={{
                                        'aria-labelledby': 'email-sub-policy',
                                    }}
                                />
                                {validationErrors.PrivacyPolicyAccepted ?
                                    <TypographyError id='email-sub-policy' sx={{ paddingLeft: '5px' }}>
                                        <SpanError>* </SpanError><span dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PrivacyPolicyText }} />
                                    </TypographyError> :
                                    <TypographyText id='email-sub-policy' sx={{ paddingLeft: '5px' }}>
                                        <SpanError>* </SpanError><span dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PrivacyPolicyText }} />
                                    </TypographyText>}
                            </> :
                            <TypographyText dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PrivacyPolicyText }} />}
                    </CheckboxGrid>}
            </>
        );
    };

    const textSignUpOnSubmit = (subscribeToWeeklyText, phoneForTextSubscription) => {
        setSubscribeToWeeklyText(subscribeToWeeklyText);
        setShowTsuModal(false);
        setShowTextSignUp(false);
        if (phoneForTextSubscription && subscribeToWeeklyText) {
            updateRegistrationPhone(phoneForTextSubscription);
        }
        if (redirectURL) {
            window.location.href = redirectURL;
        }
    };

    const externalAuthClick = (link) => {
        window.location.href = link + (formData.EmailSubAndAcknowledgement.SubscribeToWeeklyUpdate ? '&stwu=1' : '')
    };

    const updateRegistrationPhone = (Phone) => {
        let registrationPhone = "";
        if (!formData.Phone) {
            registrationPhone = Phone;
        }
        sandhillsRequest({
            url: routes.UpdateRegistrationPhone,
            method: 'post',
            data: {
                phone: registrationPhone,
                checkRegistrationPhoneNumber: true,
                pendingSubscriptionToken: pendingSubscriptionToken || undefined
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        })
    };

    return (
        <>
            {updateSuccessful ?
                <>
                    <TypographyTitle className='mobile-hide' variant='h3'>
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.updateregistration' />
                    </TypographyTitle>
                    <RegistrationPaper className='grey-box' sx={{ padding: { xs: '10px', md: '30px' } }} elevation={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant='h6'>
                                    <TranslationText tKey='sandhills.registration.updatesucessful' />
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TypographyText>
                                    <TranslationText tKey='sandhills.accountupdatedsuccessfully' />
                                </TypographyText>
                                {requestedPhoneVerification &&
                                    <TypographyText>
                                        <TranslationText tKey='sandhills.wewillcallyouatthenumberprovided' />
                                    </TypographyText>}
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant='contained' href={props.AccountURL} className={currentSite?.Site?.IsAuctionSite ? 'ts-button' : ''}>
                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.myaccount' />
                                </Button>
                            </Grid>
                        </Grid>
                    </RegistrationPaper>
                    {props.WidgetDisplay &&
                        <WidgetDisplay {...props.WidgetDisplay} />}
                </> :
                <>
                    {props.NewRegistration ?
                        <TypographyTitle variant='h3'>
                            <TranslationText tKey='sandhills.tradepubwebsite.mvc.register' />
                        </TypographyTitle> :
                        <TypographyTitle variant='h3' className='mobile-hide'>
                            <TranslationText tKey='sandhills.tradepubwebsite.mvc.updateregistration' />
                        </TypographyTitle>} 
                    <RegistrationPaper className='grey-box' sx={{ padding: { xs: '10px', md: '30px' } }} elevation={2}>
                        <h4><TranslationText tKey='sandhills.myregistration' /></h4>
                        <form>
                            <ValidationErrors errors={validationErrors} />
                            <Grid container spacing={2}>
                                {props.HighlightWeeklyEmailSubscription &&
                                    <SubscriptionAndAcknowledgement />}
                                {props.TiedLead &&
                                    <Grid item xs={12}>
                                        <TypographyError>
                                            <TranslationText tKey='sandhills.tradepubwebsite.mvc.pleaseverifythensubmit' />
                                        </TypographyError>
                                    </Grid>}
                                {props.FromHomepageText &&
                                    <Grid item xs={12}>
                                        <TypographyError dangerouslySetInnerHTML={{ __html: props.FromHomepageText }} />
                                    </Grid>}
                                {props.AdditionalInfoRequired && !props.NewRegistration &&
                                    <Grid item xs={12}>
                                        <TypographyError>
                                            <TranslationText tKey='sandhills.registration.youmustupdateyourregistration' />.
                                        </TypographyError>
                                    </Grid>}

                                {props.NewRegistration && !props.MobURL && !props.ExternalAuthLoginModel.SkipThirdPartyLoginLinks &&
                                    <Grid item xs={12}>
                                        <Stack sx={{ alignItems: 'center' }} spacing={1}>
                                            {props.ExternalAuthLoginModel.ShowFacebookLoginButton &&
                                                <Button
                                                    variant='contained'
                                                    sx={{ backgroundColor: '#1877F2', color: 'white', width: '300px', height: '36px', ':hover': { backgroundColor: '#1877F2' } }}
                                                    onClick={() => externalAuthClick(props.ExternalAuthLoginModel.FacebookLoginLink)}
                                                    startIcon={<img style={{ maxHeight: "32px", marginRight: "auto", padding: "4px", position: "absolute", top: "2px", left: "2px" }} src={props.ExternalAuthLoginModel.FacebookLoginIcon} alt="Facebook Icon Image" />}>
                                                    <TranslationText tKey='sandhills.sso.loginwithfacebook' />
                                                </Button>
                                            }
                                            {props.ExternalAuthLoginModel.ShowGoogleLoginButton &&
                                                <Button
                                                    variant='contained'
                                                    sx={{ backgroundColor: '#4285F4', color: 'white', width: '300px', height: '36px', ':hover': { backgroundColor: '#4285F4' } }}
                                                    onClick={() => externalAuthClick(props.ExternalAuthLoginModel.GoogleLoginLink)}
                                                    startIcon={<img style={{ maxHeight: "32px", marginRight: "auto", position: "absolute", top: "2px", left: "2px" }} src={props.ExternalAuthLoginModel.GoogleLoginIcon} alt="Google Icon Image" />}>
                                                    <TranslationText tKey='sandhills.sso.signinwithgoogle' />
                                                </Button>
                                            }
                                            {props.ExternalAuthLoginModel.ShowAppleLoginButton &&
                                                <Button
                                                    variant='contained'
                                                    sx={{ backgroundColor: '#000', color: 'white', width: '300px', height: '36px', ':hover': { backgroundColor: '#000' } }}
                                                    onClick={() => externalAuthClick(props.ExternalAuthLoginModel.AppleLoginLink)}
                                                    startIcon={<img style={{ maxHeight: "32px", marginRight: "auto", padding: "6px", position: "absolute", top: "2px", left: "2px" }} src={props.ExternalAuthLoginModel.AppleLoginIcon} alt="Apple Icon Image" />}>
                                                    <TranslationText tKey='sandhills.sso.signinwithapple' />
                                                </Button>
                                            }
                                        </Stack>
                                        <p style={{marginTop: "25px", marginBottom: "0px", fontSize:'14px', display:'inline-block'}} dangerouslySetInnerHTML={{ __html: props.ExternalAuthLoginModel.ThirdPartyTermsAndPolicyAcceptance }} />
                                        <Divider sx={{ marginTop: "30px" }}><TranslationText tKey='sandhills.tradepubwebsite.mvc.or' /></Divider>
                                    </Grid>}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        id='Email'
                                        name='Email'
                                        value={formData.Email}
                                        onChange={onChange}
                                        onBlur={checkEmail}
                                        label={requiredLabel(translator.translate('sandhills.email'))}
                                        placeholder={translator.translate('sandhills.thiswillbeyourusername')}
                                        error={getEmailHelperText() ? true : false}
                                        helperText={getEmailHelperText()}
                                        inputRef={element => inputReferences.current['Email'] = element}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        id='Firstname'
                                        name='Firstname'
                                        value={formData.Firstname}
                                        onChange={onChange}
                                        label={requiredLabel(translator.translate('sandhills.tradepubwebsite.mvc.firstname'))}
                                        error={validationErrors.Firstname ? true : false}
                                        helperText={validationErrors.Firstname}
                                        inputRef={element => inputReferences.current['Firstname'] = element}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        id='Lastname'
                                        name='Lastname'
                                        value={formData.Lastname}
                                        onChange={onChange}
                                        label={requiredLabel(translator.translate('sandhills.tradepubwebsite.mvc.lastname'))}
                                        error={validationErrors.Lastname ? true : false}
                                        helperText={validationErrors.Lastname}
                                        inputRef={element => inputReferences.current['Lastname'] = element}
                                    />
                                </Grid>
                                {props.NewRegistration &&
                                    <Grid item xs={12}>
                                        <PasswordField
                                            fullWidth
                                            size='small'
                                            id='Password'
                                            name='Password'
                                            value={formData.Password}
                                            onChange={onChange}
                                            label={requiredLabel(translator.translate('sandhills.password'))}
                                            placeholder={translator.translate('sandhills.registration.api.passwordeightcharacters')}
                                            helperText={validationErrors.Password}
                                            ref={element => inputReferences.current['Password'] = element}
                                        />
                                    </Grid>}
                                {displayAdditionalInfoOption() &&
                                    <CheckboxGrid item xs={12}>
                                        <CheckboxField
                                            size='small'
                                            checked={provideAdditionalInfo}
                                            onChange={(e) => setProvideAdditionalInfo(e.target.checked)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    setProvideAdditionalInfo(provideAdditionalInfo => !provideAdditionalInfo);
                                                }
                                            }}
                                            inputProps={{
                                                'aria-labelledby': 'publication-sub',
                                            }}
                                        />
                                        <TypographyText id='publication-sub' sx={{ paddingLeft: '5px' }}>
                                            {props.NewRegistration ?
                                                <TranslationText tKey='sandhills.receiveafreemagazine' replaceValues={[props.PublicationName]} /> :
                                                <TranslationText tKey='sandhills.provideadditionalwaystocontactyou' />}
                                        </TypographyText>
                                    </CheckboxGrid>}
                                {provideAdditionalInfo &&
                                    <>
                                        <Grid item xs={12} md={6}>
                                            {(countries?.length > 1) ?
                                                <Combobox
                                                    id='Country'
                                                    name='Country'
                                                    value={formData.Country}
                                                    onChange={onCountryChange}
                                                    onBlur={onAddressBlur}
                                                    getOptionLabel={getOptionLabel}
                                                    label={requiredLabel(translator.translate('sandhills.country'))}
                                                    helperText={validationErrors.Country}
                                                    ref={element => inputReferences.current['Country'] = element}
                                                    isOptionEqualToValue={isOptionEqualToValue}
                                                    options={getOptions(countries)}
                                                /> :
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='Country'
                                                    name='Country'
                                                    value={formData.Country}
                                                    label={requiredLabel(translator.translate('sandhills.country'))}
                                                    error={validationErrors.Country ? true : false}
                                                    helperText={validationErrors.Country}
                                                    inputRef={element => inputReferences.current['Country'] = element}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={1}>
                                                <PhoneField
                                                id='Phone'
                                                name='Phone'
                                                size='small'
                                                value={formData.Phone}
                                                onChange={onChange}
                                                label={requiredLabel(translator.translate('sandhills.phonenumber'))}
                                                error={validationErrors.Phone ? true : false}
                                                helperText={validationErrors.Phone}
                                                inputRef={element => inputReferences.current['Phone'] = element}
                                                intlTelInput={input => intlTelInput.current = input}
                                                fullWidth
                                                sx={{height: '48px'}}
                                                />
                                            <span style={{fontSize: '14px'}}>*<TranslationText tKey='sandhills.yourphonemaybeusedforcalls' /></span>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            {props.GoogleAutoCompleteAPIKey ?
                                                <GoogleAutoComplete
                                                    fullWidth
                                                    size='small'
                                                    id='Address1'
                                                    name='Address1'
                                                    value={formData.Address1}
                                                label={requiredLabel(translator.translate('sandhills.mobileapps.ios.physicaladdress'))}
                                                    helperText={validationErrors.Address1}
                                                    ref={element => inputReferences.current['Address1'] = element}
                                                    apiKey={props.GoogleAutoCompleteAPIKey}
                                                    onBlur={onAddressBlur}
                                                    onPlaceChange={onPlaceChange}
                                                    onChange={onAddressChange}
                                                /> :
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='Address1'
                                                    name='Address1'
                                                    value={formData.Address1}
                                                    label={requiredLabel(translator.translate('sandhills.mobileapps.ios.physicaladdress'))}
                                                    error={validationErrors.Address1 ? true : false}
                                                    helperText={validationErrors.Address1}
                                                    inputRef={element => inputReferences.current['Address1'] = element}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />}
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                size='small'
                                                id='Address2'
                                                name='Address2'
                                                value={formData.Address2}
                                                label={translator.translate('sandhills.tradepubwebsite.mvc.addressline2')}
                                                onBlur={onAddressBlur}
                                                onChange={onAddressChange}
                                            />
                                        </Grid>
                                        {(formData.Country && !(formData.Country.toUpperCase() == 'USA' || formData.Country.toUpperCase() == 'CANADA')) &&
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='Address3'
                                                    name='Address3'
                                                    value={formData.Address3}
                                                    label={translator.translate('sandhills.physicaladdress3')}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />
                                            </Grid>}
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                size='small'
                                                id='City'
                                                name='City'
                                                value={formData.City}
                                                label={requiredLabel(translator.translate('sandhills.city'))}
                                                error={validationErrors.City ? true : false}
                                                helperText={validationErrors.City}
                                                inputRef={element => inputReferences.current['City'] = element}
                                                onBlur={onAddressBlur}
                                                onChange={onAddressChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            {formData.StateRequired ?
                                                <Combobox
                                                    id='State'
                                                    name='State'
                                                    value={formData.State}
                                                    onChange={onStateChange}
                                                    onBlur={onAddressBlur}
                                                    getOptionLabel={getOptionLabel}
                                                    label={requiredLabel(translator.translate('sandhills.stateprovince'))}
                                                    helperText={validationErrors.State}
                                                    ref={element => inputReferences.current['State'] = element}
                                                    isOptionEqualToValue={isOptionEqualToValue}
                                                    options={getOptions(formData.States)}
                                                    onOpen={() => getStatesByCountry(formData.Country)}
                                                /> :
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='State'
                                                    name='State'
                                                    value={formData.StateTyped}
                                                    label={translator.translate('sandhills.stateprovince')}
                                                    inputRef={element => inputReferences.current['State'] = element}
                                                    onBlur={onAddressBlur}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setValidAddress(false);
                                                        setFormData(previousState => {
                                                            return {
                                                                ...previousState,
                                                                StateTyped: value,
                                                                State: ''
                                                            };
                                                        });
                                                        setValidationErrors(previousState => {
                                                            return {
                                                                ...previousState,
                                                                State: ''
                                                            };
                                                        });
                                                    }}
                                                />}
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                size='small'
                                                id='PostalCode'
                                                name='PostalCode'
                                                value={formData.PostalCode}
                                                label={formData.PostalCodeRequired ? requiredLabel(translator.translate('sandhills.tradepubwebsite.mvc.postalcode')) : translator.translate('sandhills.tradepubwebsite.mvc.postalcode')}
                                                error={(formData.PostalCodeRequired && validationErrors.PostalCode) ? true : false}
                                                helperText={formData.PostalCodeRequired ? validationErrors.PostalCode : ''}
                                                inputRef={element => inputReferences.current['PostalCode'] = element}
                                                onBlur={onAddressBlur}
                                                onChange={onAddressChange}
                                            />
                                        </Grid>
                                        {props.NewRegistration &&
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='CompanyName'
                                                    name='CompanyName'
                                                    value={formData.CompanyName}
                                                    onChange={onChange}
                                                    label={translator.translate('sandhills.companyname')}
                                                />
                                        </Grid>}

                                    {(currentSite?.Site.IsBidcaller || currentSite?.Site.IsAuctionTime) && (formData.Country && formData.Country.toUpperCase() == 'MEXICO') && (!props.HasSubmittedDealerReference) &&
                                        <>
                                        <Grid item xs={12}>
                                            <h4><TranslationText tKey='sandhills.tradesites.mvc.dealerreference' /></h4>
                                        </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='DealerReferenceDealerName'
                                                    name='DealerReferenceDealerName'
                                                    value={formData.DealerReferenceDealerName}
                                                    onChange={onChange}
                                                    label={requiredLabel(translator.translate('sandhills.dealername'))}
                                                    error={validationErrors.DealerReferenceDealerName ? true : false}
                                                    helperText={validationErrors.DealerReferenceDealerName}
                                                    inputRef={element => inputReferences.current['DealerReferenceDealerName'] = element}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='DealerReferenceContactName'
                                                    name='DealerReferenceContactName'
                                                    value={formData.DealerReferenceContactName}
                                                    onChange={onChange}
                                                label={requiredLabel(translator.translate('sandhills.tradepubwebsite.mvc.contactname'))}
                                                    error={validationErrors.DealerReferenceContactName ? true : false}
                                                    helperText={validationErrors.DealerReferenceContactName}
                                                    inputRef={element => inputReferences.current['DealerReferenceContactName'] = element}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                {(countries?.length > 1) ?
                                                    <Combobox
                                                        id='DealerReferenceCountry'
                                                        name='DealerReferenceCountry'
                                                        value={formData.DealerReferenceCountry}
                                                        onChange={onDealerCountryChange}
                                                        onBlur={onAddressBlur}
                                                        getOptionLabel={getOptionLabel}
                                                        label={requiredLabel(translator.translate('sandhills.country'))}
                                                        helperText={validationErrors.DealerReferenceCountry}
                                                        ref={element => inputReferences.current['DealerReferenceCountry'] = element}
                                                        isOptionEqualToValue={isOptionEqualToValue}
                                                        options={getOptions(countries)}
                                                    /> :
                                                    <TextField
                                                        fullWidth
                                                        size='small'
                                                        id='DealerReferenceCountry'
                                                        name='DealerReferenceCountry'
                                                        value={formData.DealerReferenceCountry}
                                                        label={requiredLabel(translator.translate('sandhills.country'))}
                                                        error={validationErrors.DealerReferenceCountry ? true : false}
                                                        helperText={validationErrors.DealerReferenceCountry}
                                                        inputRef={element => inputReferences.current['DealerReferenceCountry'] = element}
                                                        onBlur={onAddressBlur}
                                                        onChange={onAddressChange}
                                                    />}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Stack spacing={1}>
                                                    <PhoneField
                                                        id='DealerReferencePhone'
                                                        name='DealerReferencePhone'
                                                        size='small'
                                                        value={formData.DealerReferencePhone}
                                                        onChange={onChange}
                                                        label={requiredLabel(translator.translate('sandhills.phonenumber'))}
                                                        error={validationErrors.DealerReferencePhone ? true : false}
                                                        helperText={validationErrors.DealerReferencePhone}
                                                        inputRef={element => inputReferences.current['DealerReferencePhone'] = element}
                                                        intlTelInput={input => dealerIntlTelInput.current = input}
                                                        fullWidth
                                                        sx={{ height: '48px' }}
                                                    />
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                {props.GoogleAutoCompleteAPIKey ?
                                                    <GoogleAutoComplete
                                                        fullWidth
                                                        size='small'
                                                    id='DealerReferenceAddress1'
                                                    name='DealerReferenceAddress1'
                                                    value={formData.DealerReferenceAddress1}
                                                        label={requiredLabel(translator.translate('sandhills.mobileapps.ios.physicaladdress'))}
                                                        helperText={validationErrors.DealerReferenceAddress1}
                                                    ref={element => inputReferences.current['DealerReferenceAddress1'] = element}
                                                        apiKey={props.GoogleAutoCompleteAPIKey}
                                                        onBlur={onAddressBlur}
                                                        onPlaceChange={onPlaceChange}
                                                        onChange={onAddressChange}
                                                    /> :
                                                    <TextField
                                                        fullWidth
                                                        size='small'
                                                    id='DealerReferenceAddress1'
                                                    name='DealerReferenceAddress1'
                                                    value={formData.DealerReferenceAddress1}
                                                        label={requiredLabel(translator.translate('sandhills.mobileapps.ios.physicaladdress'))}
                                                    error={validationErrors.DealerReferenceAddress1 ? true : false}
                                                    helperText={validationErrors.DealerReferenceAddress1}
                                                    inputRef={element => inputReferences.current['DealerReferenceAddress1'] = element}
                                                        onBlur={onAddressBlur}
                                                        onChange={onAddressChange}
                                                    />}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='DealerReferenceAddress2'
                                                    name='DealerReferenceAddress2'
                                                    value={formData.DealerReferenceAddress2}
                                                    label={translator.translate('sandhills.tradepubwebsite.mvc.addressline2')}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />
                                            </Grid>
                                        <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='DealerReferenceCity'
                                                    name='DealerReferenceCity'
                                                    value={formData.DealerReferenceCity}
                                                    label={requiredLabel(translator.translate('sandhills.city'))}
                                                    error={validationErrors.DealerReferenceCity ? true : false}
                                                    helperText={validationErrors.DealerReferenceCity}
                                                    inputRef={element => inputReferences.current['DealerReferenceCity'] = element}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />
                                            </Grid>
                                        <Grid item xs={12} md={4}>
                                                {formData.DealerStateRequired ?
                                                    <Combobox
                                                        id='DealerReferenceState'
                                                        name='DealerReferenceState'
                                                        value={formData.DealerReferenceState}
                                                        onChange={onDealerStateChange}
                                                        onBlur={onAddressBlur}
                                                        getOptionLabel={getOptionLabel}
                                                        label={requiredLabel(translator.translate('sandhills.stateprovince'))}
                                                        helperText={validationErrors.DealerReferenceState}
                                                        ref={element => inputReferences.current['DealerReferenceState'] = element}
                                                        isOptionEqualToValue={isOptionEqualToValue}
                                                        options={getOptions(formData.DealerReferenceStates)}
                                                        onOpen={() => getDealerStatesByCountry(formData.DealerReferenceCountry)}
                                                    /> :
                                                    <TextField
                                                        fullWidth
                                                        size='small'
                                                        id='DealerReferenceState'
                                                        name='DealerReferenceState'
                                                        value={formData.DealerReferenceStateTyped}
                                                        label={translator.translate('sandhills.stateprovince')}
                                                        inputRef={element => inputReferences.current['DealerReferenceState'] = element}
                                                        onBlur={onAddressBlur}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setValidAddress(false);
                                                            setFormData(previousState => {
                                                                return {
                                                                    ...previousState,
                                                                    DealerReferenceStateTyped: value,
                                                                    DealerReferenceState: ''
                                                                };
                                                            });
                                                            setValidationErrors(previousState => {
                                                                return {
                                                                    ...previousState,
                                                                    DealerReferenceState: ''
                                                                };
                                                            });
                                                        }}
                                                />}
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    fullWidth
                                                    size='small'
                                                    id='DealerReferencePostalCode'
                                                    name='DealerReferencePostalCode'
                                                    value={formData.DealerReferencePostalCode}
                                                    label={formData.DealerPostalCodeRequired ? requiredLabel(translator.translate('sandhills.tradepubwebsite.mvc.postalcode')) : translator.translate('sandhills.tradepubwebsite.mvc.postalcode')}
                                                    error={(formData.DealerPostalCodeRequired && validationErrors.DealerReferencePostalCode) ? true : false}
                                                    helperText={formData.DealerPostalCodeRequired ? validationErrors.DealerReferencePostalCode : ''}
                                                    inputRef={element => inputReferences.current['DealerReferencePostalCode'] = element}
                                                    onBlur={onAddressBlur}
                                                    onChange={onAddressChange}
                                                />
                                            </Grid>
                                        </>}
                                    </>}
                                {!props.HighlightWeeklyEmailSubscription &&
                                    <SubscriptionAndAcknowledgement />}
                                {props.SubmissionAcceptance &&
                                    <Grid item xs={12}>
                                        <TypographyText dangerouslySetInnerHTML={{ __html: props.SubmissionAcceptance }} />
                                    </Grid>}
                                {props.FR8StarLegalText &&
                                    <Grid item xs={12}>
                                        <TypographyText dangerouslySetInnerHTML={{ __html: props.FR8StarLegalText }} />
                                    </Grid>}
                                {validationErrors.Error &&
                                    <Grid item xs={12}>
                                        <TypographyError align='center'>{validationErrors.Error}</TypographyError>
                                    </Grid>}
                                {formData.BannedCountryError &&
                                    <Grid item xs={12}>
                                        <TypographyError align='center' dangerouslySetInnerHTML={{ __html: formData.BannedCountryError }} />
                                    </Grid>}
                                {props.NewRegistration ?
                                    <>
                                        <Grid item xs={12}>
                                            <SubmitButton color={currentSite.ThemeOverride} variant='contained' onClick={submitForm} disabled={saveRegistration} className={'template-branded-button ' + (currentSite?.Site?.IsAuctionSite ? 'ts-button' : '')}>
                                                {props.BidderRegistration ?
                                                    <TranslationText tKey='sandhills.continue' />
                                                    :
                                                    <TranslationText tKey='sandhills.tradepubwebsite.mvc.submit' />
                                                }
                                            </SubmitButton>
                                        </Grid>
                                        {props.Captcha &&
                                            <Grid item xs={12}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                                    <SandhillsReCAPTCHA
                                                        ref={recaptchaRef}
                                                        sitekey={props.Captcha.Key}
                                                        onChange={onSubmit}
                                                        onCaptchaClosedBeforeCompletion={resetSubmit}
                                                        captchaOpen={captchaOpen}
                                                    />
                                                </Box>
                                            </Grid>}
                                    </> :
                                    <Grid item xs={12}>
                                        <SubmitButton color={currentSite.ThemeOverride} variant='contained' onClick={submitForm} disabled={saveRegistration} className={'template-branded-button ' + (currentSite?.Site?.IsAuctionSite ? 'ts-button' : '')}>
                                            {props.BidderRegistration ?
                                                <TranslationText tKey='sandhills.continue' />
                                                :
                                                <TranslationText tKey='sandhills.photosvideos.savechanges' />
                                            }
                                        </SubmitButton>
                                    </Grid>}
                            </Grid>
                        </form>
                    </RegistrationPaper>
                    {props.WidgetDisplay &&
                        <WidgetDisplay {...props.WidgetDisplay} />}
                    {saveRegistration &&
                        <LoadingRing backdrop />}
                </>}
            {showAddressValidationModal &&
                <AddressValidationModal
                    toggleModal={() => setShowAddressValidationModal(!showAddressValidationModal)}
                    recommendedAddress={recommendedAddress}
                    formData={formData}
                    closeModalAndSubmit={closeModalAndSubmit}
                />}
            {showMFAModal &&
                <MultiFactorModal
                    {...props}
                    MultifactorAuthenticationValues={{
                        EmailAddresses: [],
                        PhoneNumbers: [{
                            Phone: formData.Phone,
                            CanSendTexts: mfaCanSendTexts,
                            Source: mfaMethodSource.Input
                        }]
                    }}
                    forceNewMFA={true}
                    toggleModal={() => toggleMultiFactorModal(false)}
                    onMultiFactorAuthorized={onMultiFactorAuthorized}
                    displayMode={mfaDisplayModes.RegistrationForm}
                    textLoading={!mfaPhoneValidationFinished}
                    textVerificationDisabled={!mfaCanSendTexts}
                />}
            {showTsuModal && <TextSignUpModal {...props.TextSignUpModel} CentralUserAuthId={centralUserAuthid} pendingSubscriptionToken={pendingSubscriptionToken} setShowTsuModal={setShowTsuModal} onSubmit={textSignUpOnSubmit} />}
        </>
    );
};

export default RegistrationForm;